import AccountStatus from "enums/AccountStatus";
import CardStatusType from "enums/CardStatusType";
import {
  AstradaConnection,
  AstradaTransactionRow,
  EnrolledCard,
} from "./types";
import AccountType from "enums/AccountType";

/**
 * MOCK DATA for the Astrada Linked Accounts screen, standing in until PR #1144 lands.
 *
 *   localStorage.setItem("outpave-astrada-mock", "true");  // two connections and their 10 cards
 *   localStorage.setItem("outpave-astrada-mock", "empty"); // no connections
 *   localStorage.removeItem("outpave-astrada-mock");       // off, query for real
 *
 * Delete this module and its call sites once #1144 is merged. The flag also gates the BYOC
 * dashboard's mock insights, which then show only their real rows. See README.md.
 */

const MOCK_STORAGE_KEY = "outpave-astrada-mock";

export type AstradaMockMode = "off" | "populated" | "empty";

/**
 * Which fixture set to serve, if any.
 *
 * "empty" is its own mode rather than an empty fixture array because the queries still have to
 * be skipped: without mocks the page would fall through to a real request that errors until
 * #1144, which looks the same on screen but is not the empty state.
 *
 * Gated on NODE_ENV as well, so a stray key cannot enable mocks in a production bundle.
 */
export const astradaMockMode = (): AstradaMockMode => {
  if (process.env.NODE_ENV === "production") return "off";
  try {
    const value = localStorage.getItem(MOCK_STORAGE_KEY);
    if (value === "empty") return "empty";
    // "true" is the original spelling and stays working.
    if (value === "true" || value === "populated") return "populated";
    return "off";
  } catch {
    // localStorage unavailable (private browsing).
    return "off";
  }
};

/** Whether the page should serve fixtures at all, in either mode. */
export const astradaMocksEnabled = (): boolean => astradaMockMode() !== "off";

const CONNECTION_ID = "mock-astrada-connection";
const CONNECTION_NAME = "Visa · JPMorgan Chase";
const AMEX_CONNECTION_ID = "mock-astrada-connection-amex";
const AMEX_CONNECTION_NAME = "American Express";

export const MOCK_CONNECTIONS: AstradaConnection[] = [
  {
    id: CONNECTION_ID,
    name: CONNECTION_NAME,
    status: AccountStatus.ACTIVE,
    networkReference: "RPIC-4417820",
    cardCount: 7,
    connectedAt: Date.parse("2026-04-12T12:00:00Z"),
    accountType: AccountType.ASTRADA_CONNECTION,
  },
  {
    id: AMEX_CONNECTION_ID,
    name: AMEX_CONNECTION_NAME,
    status: AccountStatus.ACTIVE,
    networkReference: "SE-9377120",
    cardCount: 3,
    connectedAt: Date.parse("2026-06-06T12:00:00Z"),
    accountType: AccountType.ASTRADA_CONNECTION,
  },
];

const PROGRAMS = {
  visa: {
    connectionId: CONNECTION_ID,
    connectionName: CONNECTION_NAME,
    connectionNetwork: "VISA",
  },
  amex: {
    connectionId: AMEX_CONNECTION_ID,
    connectionName: AMEX_CONNECTION_NAME,
    connectionNetwork: "AMEX",
  },
} as const;

const mockUser = (id: string, name: string, email: string) => ({
  id,
  name,
  email,
});

/** Mirrors the OUT-1523 design: 2 enrolled, 6 unassigned, 2 unenrolled. */
const card = (
  id: string,
  program: keyof typeof PROGRAMS,
  cardholderName: string,
  cardNumber: string,
  enrolled: string,
  enrollment: EnrolledCard["enrollment"],
  assignedUser: EnrolledCard["assignedUser"] = null,
): EnrolledCard => ({
  id,
  cardNumber,
  nickname: null,
  status: {
    enrolled: CardStatusType.ACTIVE,
    unassigned: CardStatusType.PENDING,
    unenrolled: CardStatusType.CANCELED,
  }[enrollment],
  cardholderName,
  assignedUser,
  enrollment,
  ...PROGRAMS[program],
  activatedAt: enrolled,
  cardIssuerId: null,
});

export const MOCK_CARDS: EnrolledCard[] = [
  card(
    "mock-card-1",
    "visa",
    "Zachary Ahlstedt",
    "2009",
    "2026-04-12",
    "enrolled",
    mockUser("mock-user-zach", "Zach Ahlstedt", "zach@outpave.com"),
  ),
  card(
    "mock-card-2",
    "visa",
    "Emily J. Cho",
    "4417",
    "2026-04-12",
    "enrolled",
    mockUser("mock-user-emily", "Emily Cho", "emily@outpave.com"),
  ),
  card(
    "mock-card-3",
    "visa",
    "David Cauthon",
    "1005",
    "2026-05-01",
    "unassigned",
  ),
  card(
    "mock-card-4",
    "visa",
    "Kristopher Camonayan",
    "7782",
    "2026-05-01",
    "unassigned",
  ),
  card(
    "mock-card-5",
    "visa",
    "Yeana M. Cho",
    "9920",
    "2026-05-14",
    "unassigned",
  ),
  card(
    "mock-card-6",
    "amex",
    "Devon Okafor",
    "7186",
    "2026-06-06",
    "unassigned",
  ),
  card("mock-card-7", "amex", "Ravi Menon", "9041", "2026-06-06", "unassigned"),
  card(
    "mock-card-8",
    "amex",
    "Sofia Duarte",
    "2889",
    "2026-06-06",
    "unassigned",
  ),
  card(
    "mock-card-9",
    "visa",
    "Lam T. Tran",
    "6634",
    "2026-05-20",
    "unenrolled",
    mockUser("mock-user-lam", "Lam Tran", "lam@outpave.com"),
  ),
  card(
    "mock-card-10",
    "visa",
    "Marcus Webb",
    "3318",
    "2026-04-12",
    "unenrolled",
  ),
];

const transaction = (
  index: number,
  date: string,
  merchant: string,
  cardholderName: string,
  amount: number,
): AstradaTransactionRow => ({
  id: `mock-transaction-${index}`,
  // The table renders this through dayjs, which is what the real transactionDate feeds too.
  transactionDate: Date.parse(`${date}T12:00:00Z`),
  merchant,
  amount,
  currency: "usd",
  cardholderName,
  accountName: CONNECTION_NAME,
});

export const MOCK_TRANSACTIONS: AstradaTransactionRow[] = [
  transaction(1, "2026-06-06", "Marriott", "Emily Reyes", 274.0),
  transaction(2, "2026-06-06", "Delta Air Lines", "Tomás Vega", 675.96),
  transaction(3, "2026-06-05", "Delta Air Lines", "Jordan Lee", 612.4),
  transaction(4, "2026-06-05", "Hilton", "David Cauthon", 549.13),
  transaction(5, "2026-06-05", "Mustang Cafe", "David Cauthon", 121.32),
  transaction(6, "2026-06-05", "United Airlines", "Sana Okafor", 742.95),
  transaction(7, "2026-06-04", "Hilton", "Maria Cruz", 388.16),
  transaction(8, "2026-06-04", "Mustang Cafe", "Sana Okafor", 109.24),
  transaction(9, "2026-06-04", "United Airlines", "Sana Okafor", 304.31),
  transaction(10, "2026-06-04", "United Airlines", "Tomás Vega", 845.91),
  transaction(11, "2026-06-03", "Home Depot", "Jordan Lee", 871.62),
  transaction(12, "2026-06-03", "Mustang Cafe", "Sana Okafor", 59.4),
  transaction(13, "2026-06-03", "Chevron", "Emily Reyes", 170.26),
  transaction(14, "2026-06-03", "Shell", "Maria Cruz", 97.55),
  transaction(15, "2026-06-02", "Shell", "David Cauthon", 74.83),
  transaction(16, "2026-06-02", "Shell", "Maria Cruz", 56.1),
  transaction(17, "2026-06-02", "Mustang Cafe", "Tomás Vega", 156.32),
  transaction(18, "2026-06-02", "Adobe", "Maria Cruz", 298.38),
  transaction(19, "2026-06-01", "Staples", "Sana Okafor", 142.09),
  transaction(20, "2026-06-01", "Chevron", "Jordan Lee", 144.0),
  transaction(21, "2026-06-01", "United Airlines", "Priya Nair", 645.07),
  transaction(22, "2026-06-01", "Shell", "Jordan Lee", 135.5),
  transaction(23, "2026-05-31", "Apple Store", "Maria Cruz", 702.77),
  transaction(24, "2026-05-30", "Uber", "Jordan Lee", 38.5),
  transaction(25, "2026-05-30", "Fastenal", "Sana Okafor", 484.36),
];
